import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { 
    Modal, 
    Loading, 
    Form, 
    Button, 
    TextArea, 
    PillsGroup, 
    Checkbox, 
    Label
} from "@gpstrackit/gtc-react-controls";
import { LogManagerConsole } from "../../../../core/logManager";
import { NotificationManager } from "Components/Notifications";
import { HasPermission } from "Core/security";

import { close, reset, saveChangeStatus } from "./redux/slice";
import colors from "Assets/sass/core/vars.scss";

export const ChangeStatusModal = ({ onClose }) => {
    const formDelete = Form.useForm();
    const { messages } = useIntl()
    const dispatch = useDispatch()
    const [values, setValues] = useState({ notes: null, visibleToDriver: false })

    const { user } = useSelector((state) => state.securityRedux);
    const { loading, open, items, result, error } = useSelector(state => state.videoChangeStatusRedux)
    LogManagerConsole("MEDIA", "DeleteVideoModal", loading, open, items)

    useEffect(() => {
        if (open)
            formDelete.setValues({ status: "Unreviewed", notes: null })
    }, [open])


    useEffect(() => {
        if (result) {

            dispatch(reset({ result: null }))

            NotificationManager.success(
                messages['videoDetail_success'],
                messages['success'],
                3000
            );

            handleClose(true)
        }
    }, [result])

    useEffect(() => {
        if (error) {

            dispatch(reset({ error: null, result: null }))

            NotificationManager.error(
                messages['videoDetail_error'],
                messages['error'],
                3000
            );

            handleClose(true)
        }
    }, [error])

    const handleClose = useCallback((refresh) => {
        dispatch(close())
        formDelete.resetValues()
        setValues({ notes: null, visibleToDriver: false })

        onClose && onClose(refresh)
        dispatch(reset())
    }, [dispatch, formDelete, onClose, setValues])

    const reviewStates = useMemo(() => [
        {
            id: "Unreviewed",
            label: messages["videoMainView_unreviewed"],
            color: colors["warning"],
        },
        {
            id: "Reviewed",
            label: messages["videoMainView_reviewed"],
            color: colors["brown"],
        },
        {
            id: "FalsePositive",
            label: messages["videoMainView_falsePositive"],
            color: colors["purple"],
        },
        {
            id: "Accepted",
            label: messages["videoMainView_accepted"],
            color: colors["green"],
        },
        {
            id: "Disputed",
            label: messages["videoMainView_disputed"],
            color: colors["red"],
        },
    ], [messages, colors]);

    const getTitle = useCallback(() => {
        if (items?.length === 1) {
            let eventName = items[0]?.eventName;
            return `${messages['videoMainView_changeStatus']} - ${eventName}`;
        } else if (items?.length > 1) {
            let total = items?.length || 0;
            return `${messages['videoMainView_changeStatus']} - ${total} ${messages['videoMainView_eventsSelected']}`;
        }
        return "";
    }, [items, messages]);

    const isDisabled = items?.length === 0 || !values?.status || !values?.notes || values?.notes?.length < 6 || loading

    return (
        <Modal
            title={getTitle()}
            closeButton
            open={open}
            position="right"
            size="small"
            handleClose={() => {
                handleClose()
            }}
            footer={
                <div style={{ alignContent: 'start' }}>
                    <Button
                        isDisabled={loading}
                        onClick={(e) => {
                            handleClose()
                        }}
                        variant="secondary"
                    >
                        {messages['cancel']}
                    </Button>
                    {"  "}
                    <Button
                        isDisabled={isDisabled}
                        
                        onClick={(e) => {
                            formDelete.submit();
                        }}
                        variant="primary"
                    >
                        {messages['save']}
                    </Button>
                </div>
            }
        >
            <div>
                {(loading) &&
                    <div style={{ paddingLeft: 20 }}>
                        <Loading full className={"loading-training-lms"} />
                    </div>
                }
                <div style={{ padding: 24 }}>
                    <Form
                        ref={formDelete.ref}
                        onValidSubmit={(_values) => {
                            dispatch(saveChangeStatus({ ids: items.map(x => { return { rowId: x.id, deviceId: x.deviceId } }), notes: values.notes, status: values.status, visibleToDriver: values.visibleToDriver }))
                        }}
                        onChangeValues={(_values) => {
                            setValues({ ...values, notes: _values.notes })
                        }}
                    >

                        <div style={{ marginTop: 8, marginBottom: 5 }}>
                            <Form.Item
                                name="status"
                                label={messages['videoDetail_reviewStatus']}
                                rules={[{ required: true, message: 'required_field' }]}
                            >
                                <PillsGroup
                                    onClick={(item) => {
                                        formDelete.setValues({ status: item.id });
                                        setValues({ ...values, status: item.id })
                                    }}
                                    options={reviewStates}
                                    size="dynamic"
                                    style={{
                                        margin: 3
                                    }}
                                    values={[reviewStates.find(x => x.id == values.status)]}
                                    multicolor={true}
                                />
                            </Form.Item>
                        </div>
                        <Form.Item
                            name="notes"
                            label={messages['videoDetail_notes']}
                            rules={[{ required: true, message: 'required_field' }]}
                        >
                            <TextArea
                                placeholder={messages['videoDetail_notes']}
                                maxLength={300}
                            />
                        </Form.Item>
                        <Label size="sm" style={{
                            color: 'gray',
                            marginTop: '-15px',
                            position: 'absolute'
                        }}>{messages['videoDetail_notes_maxLength']}</Label>
                        {
                            HasPermission(user, ["vidrw"], [261]) &&
                            <div>
                                <Checkbox
                                    label={messages["videoDetail_visibleToDriver"]}
                                    checked={values?.visibleToDriver ? true : false}
                                    onChange={(event) => {
                                        setValues({ ...values, visibleToDriver: event });
                                    }}
                                    size="md"
                                />
                            </div>
                        }
                    </Form>
                </div>
            </div>
        </Modal>
    )
}

export default ChangeStatusModal